/** Terms of Use body (title and date rendered by LegalPageLayout). */
export const TERMS_OF_USE_BODY = `
These Terms of Use (“Terms”) govern access to and use of the website, software platform, products, applications, and services provided under Aivory EMS, an intellectual property and software platform owned and operated by AivoryLab Pte. Ltd. (“AivoryLab”, “Aivory EMS”, “Company”, “we”, “our”, or “us”).

By accessing, browsing, registering for, or using Aivory EMS, you acknowledge that you have read, understood, and agreed to be legally bound by these Terms.

If you do not agree to these Terms, you must discontinue use of the Services immediately.

---

# 1. Company Information

## Legal Entity

Aivory EMS is owned, developed, licensed, and operated by:

**AivoryLab Pte. Ltd.**
Singapore

Website:
[https://aivoryems.com](https://aivoryems.com)

For legal or compliance matters:
[privacy@aivorylab.com](mailto:privacy@aivorylab.com)

---

# 2. Definitions

For the purposes of these Terms:

* “Services” refers to the Aivory EMS platform, website, software, applications, event technologies, integrations, and related services.
* “User” refers to any individual or entity accessing or using the Services.
* “Organizer” refers to customers or entities using Aivory EMS to manage events.
* “Content” refers to information, materials, media, data, text, graphics, branding, and uploads made available through the Services.
* “Platform” refers to the Aivory EMS software ecosystem.

---

# 3. Eligibility

Users must:

* be legally capable of entering binding agreements;
* comply with applicable laws and regulations; and
* provide accurate and complete information when using the Services.

Where users access the Services on behalf of an organization, the user represents that they have authority to bind that organization to these Terms.

---

# 4. Scope of Services

Aivory EMS provides event management and related technology services, including but not limited to:

* event registration
* attendee management
* ticketing
* event websites
* onsite operations
* badge printing
* hybrid and virtual event services
* networking features
* engagement tools
* analytics and reporting
* exhibitor and sponsor management
* integrations with third-party systems

The Services may evolve, change, or be updated periodically.

---

# 5. User Accounts

Users may be required to create accounts to access certain Services.

Users are responsible for:

* maintaining confidentiality of login credentials;
* restricting unauthorized access to their accounts;
* ensuring information remains accurate and current; and
* all activities conducted under their accounts.

Users must notify AivoryLab immediately of any suspected unauthorized access or security breach.

---

# 6. Acceptable Use

Users agree not to:

* violate applicable laws or regulations;
* misuse the Services;
* interfere with platform operations;
* attempt unauthorized access to systems or data;
* distribute malicious code or harmful technologies;
* infringe intellectual property rights;
* impersonate individuals or organizations;
* upload unlawful, abusive, defamatory, fraudulent, or misleading content;
* disrupt events, systems, or platform security;
* reverse engineer or attempt to extract source code from the platform except where expressly permitted by law.

AivoryLab reserves the right to suspend or terminate access for violations of these Terms.

---

# 7. Intellectual Property Rights

## Ownership

Aivory EMS, including its:

* software
* source code
* architecture
* workflows
* branding
* trademarks
* logos
* interfaces
* documentation
* visual designs
* platform features
* proprietary methodologies
* content

are proprietary intellectual property owned or licensed by AivoryLab Pte. Ltd.

All rights not expressly granted are reserved.

---

## Restrictions

Users may not:

* reproduce;
* distribute;
* modify;
* sublicense;
* commercially exploit;
* reverse engineer;
* copy;
* scrape;
* republish; or
* create derivative works

from the Services without prior written authorization.

---

## Customer Content

Users retain ownership of content uploaded to the platform.

By uploading or submitting content, users grant AivoryLab a non-exclusive, limited license to host, process, display, and use such content solely for the purpose of operating and delivering the Services.

Users represent that they possess the rights necessary to upload and use submitted content.

---

# 8. Event Organizer Responsibilities

Event organizers using Aivory EMS are responsible for:

* compliance with applicable laws;
* obtaining attendee consent where required;
* ensuring lawful collection and processing of personal data;
* accuracy of event-related information;
* compliance with payment, ticketing, and refund obligations;
* managing event-specific policies and disclosures.

AivoryLab acts as a technology provider and platform operator unless otherwise agreed in writing.

---

# 9. Payments & Commercial Terms

Where applicable:

* fees and subscription terms shall be governed by separate commercial agreements;
* organizers are responsible for applicable taxes and charges;
* unpaid fees may result in suspension or termination of Services.

Unless otherwise stated in writing, fees paid are non-refundable.

---

# 10. Third-Party Services & Integrations

The Services may integrate with or link to third-party platforms and providers.

AivoryLab does not control third-party services and is not responsible for:

* availability;
* functionality;
* content;
* security;
* policies; or
* practices

of third-party providers.

Users remain responsible for reviewing and complying with applicable third-party terms.

---

# 11. Privacy & Data Protection

Use of the Services is subject to the Aivory EMS Privacy Policy.

By using the Services, users acknowledge that personal data may be collected, processed, stored, and transferred in accordance with the Privacy Policy and applicable laws.

---

# 12. Service Availability

AivoryLab may:

* update;
* modify;
* suspend;
* restrict; or
* discontinue

parts of the Services periodically for maintenance, operational, security, or business reasons.

We aim to maintain commercially reasonable platform availability but do not guarantee uninterrupted or error-free operation.

---

# 13. Suspension & Termination

AivoryLab reserves the right to suspend or terminate access to the Services where:

* users violate these Terms;
* security risks are identified;
* unlawful activity is suspected;
* payment obligations remain unpaid; or
* continued access may adversely impact platform operations.

Users may discontinue use of the Services at any time.

Certain obligations under these Terms shall survive termination, including intellectual property protections and compliance obligations.

---

# 14. Confidentiality

Users agree not to disclose confidential or proprietary information obtained through the Services without authorization.

Confidential information includes:

* technical information
* platform documentation
* commercial information
* software architecture
* pricing information
* security measures
* non-public operational materials

unless such information is already publicly available through lawful means.

---

# 15. Indemnification

Users agree to indemnify and hold harmless AivoryLab Pte. Ltd., its directors, employees, affiliates, and partners from claims, damages, losses, liabilities, costs, or expenses arising from:

* misuse of the Services;
* violation of these Terms;
* infringement of third-party rights; or
* unlawful conduct by the user.

---

# 16. Changes to Terms

AivoryLab reserves the right to modify or update these Terms periodically.

Updated versions will be published on the website with a revised effective date.

Continued use of the Services after updates constitutes acceptance of the revised Terms.

---

# 17. Governing Law

These Terms shall be governed by and construed in accordance with the laws of Singapore.

Any disputes arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Singapore.

---

# 18. Compliance with Laws

Users agree to comply with all applicable laws and regulations relating to:

* data protection;
* intellectual property;
* event operations;
* online conduct;
* export controls; and
* electronic communications.

---

# 19. Electronic Communications

By using the Services, users consent to receiving electronic communications relating to:

* account activity;
* event updates;
* platform notifications;
* legal notices;
* operational communications.

Users remain responsible for maintaining valid contact information.

---

# 20. Entire Agreement

These Terms, together with the Privacy Policy and any applicable commercial agreements, constitute the entire agreement between users and AivoryLab regarding use of the Services.

---

# 21. Contact Information

For legal, compliance, or contractual matters, please contact:

## AivoryLab Pte. Ltd.

Website:
[https://aivoryems.com](https://aivoryems.com)

Email:
[privacy@aivorylab.com](mailto:privacy@aivorylab.com)

Singapore
`.trim();
